import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings, Palette, Layout, Megaphone, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface SettingsData {
  // Branding & Theming
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  logo: string;
  customCSS: string;
  
  // Page Layout
  showQueue: boolean;
  showSearch: boolean;
  showSongList: boolean;
  sortBy: string;
  alignment: string;
  
  // Announcements
  bannerAd: string;
  announcement: string;
  announcementStart: string;
  announcementEnd: string;
  
  // Queue Management
  requestLimit: number;
  autoRemove: boolean;
  allowRequests: boolean;
}

const HostSettings = ({ host }: { host?: any } = {}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  
  // Check tier-based restrictions
  const isEnterprise = host?.subscription_level === 'enterprise';
  const isRegular = host?.subscription_level === 'regular';
  const isBasic = host?.subscription_level === 'basic';
  
  const [settings, setSettings] = useState<SettingsData>({
    primaryColor: "",
    secondaryColor: "",
    backgroundColor: "",
    textColor: "",
    logo: "",
    customCSS: "",
    showQueue: true,
    showSearch: true,
    showSongList: true,
    sortBy: "title",
    alignment: "left",
    bannerAd: "",
    announcement: "",
    announcementStart: "",
    announcementEnd: "",
    requestLimit: 0,
    autoRemove: false,
    allowRequests: true,
  });

  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Load settings from database
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const hostId = localStorage.getItem('hostId');
      if (hostId) {
        // First get the venue for this host
        const { data: venueHostData, error: venueHostError } = await (supabase
          .from('venue_hosts') as any)
          .select('venue_id, venues (*)')
          .eq('host_id', hostId)
          .single();

        if (venueHostError || !venueHostData) {
          console.log('No venue found for host or error:', venueHostError);
          return;
        }

        const venue = venueHostData.venues as any;
        if (venue) {
          setSettings({
            primaryColor: venue.primary_color || "#3b82f6",
            secondaryColor: venue.secondary_color || "#1e40af",
            backgroundColor: venue.secondary_color || "#1e40af",
            textColor: "#ffffff",
            logo: venue.logo_url || "",
            customCSS: "",
            showQueue: true,
            showSearch: true,
            showSongList: true,
            sortBy: "title",
            alignment: "left",
            bannerAd: "",
            announcement: venue.welcome_message || "",
            announcementStart: "",
            announcementEnd: "",
            requestLimit: typeof venue.request_limit === 'number' ? venue.request_limit : 0,
            autoRemove: !!venue.auto_remove,
            allowRequests: venue.allow_requests !== false,
          });
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Resolve the caller's venue: venue_admins via the venue_hosts junction,
  // plain hosts via hosts.venue_id (returned by get_host_self).
  const resolveVenueId = async (): Promise<string | null> => {
    const hostId = localStorage.getItem('hostId');
    if (!hostId) return null;

    const { data: venueHostData } = await supabase
      .from('venue_hosts')
      .select('venue_id')
      .eq('host_id', hostId)
      .maybeSingle();
    if (venueHostData?.venue_id) return venueHostData.venue_id;

    const { data } = await (supabase.rpc as any)('get_host_self', {
      p_token: localStorage.getItem('hostSessionToken'),
    });
    const hostData = (Array.isArray(data) ? data[0] : data) as any;
    return hostData?.venue_id ?? null;
  };

  const handleSave = async () => {
    try {
      const venueId = await resolveVenueId();
      if (!venueId) throw new Error('Could not find venue for host');

      // Branding + welcome message — via RPC. A direct venues UPDATE runs as the
      // anon role (hosts use a custom session token, not Supabase Auth) and is
      // silently denied by RLS (0 rows, no error), so logo_url never persists.
      const { error: brandingError } = await (supabase.rpc as any)('update_venue_branding', {
        p_session_token: localStorage.getItem('hostSessionToken'),
        p_venue_id: venueId,
        p_primary_color: settings.primaryColor,
        p_secondary_color: settings.secondaryColor,
        p_logo_url: settings.logo,
        p_welcome_message: settings.announcement,
      });
      if (brandingError) throw brandingError;

      // Queue rules — via RPC so both venue_admin and host paths are safe
      const { error: rulesError } = await (supabase.rpc as any)('update_venue_queue_rules', {
        p_session_token: localStorage.getItem('hostSessionToken'),
        p_venue_id: venueId,
        p_request_limit: Number.isFinite(Number(settings.requestLimit)) && Number(settings.requestLimit) > 0
          ? Number(settings.requestLimit)
          : 100,
        p_allow_requests: settings.allowRequests,
        p_auto_remove: !!settings.autoRemove,
      });
      if (rulesError) throw rulesError;

      toast({
        title: "Settings Saved",
        description: "Your singer page settings have been updated successfully!",
      });
      setOpen(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    }
  };

  // Allowed logo image types → canonical extension. SVG is intentionally
  // excluded (it can carry scripts → stored XSS when served from the bucket).
  const ALLOWED_LOGO_TYPES: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
  };
  const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB

  const handleLogoFile = async (file?: File | null) => {
    if (!file) return;

    // Client-side validation is UX only — the logo-upload edge function
    // re-validates type, size, and the real image magic bytes server-side
    // before uploading with the service role. The bucket is no longer
    // writable with the anon key.
    const ext = ALLOWED_LOGO_TYPES[file.type];
    if (!ext) {
      toast({ title: 'Unsupported file', description: 'Upload a PNG, JPG, WebP or GIF image.', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast({ title: 'Image too large', description: 'Logo must be 2 MB or smaller.', variant: 'destructive' });
      return;
    }

    setUploadingLogo(true);
    try {
      const sessionToken = localStorage.getItem('hostSessionToken');
      if (!sessionToken) throw new Error('Not logged in');

      const venueId = await resolveVenueId();
      if (!venueId) throw new Error('Could not find venue for host');

      // The host is authenticated and authorized server-side from this token;
      // the function derives the storage path and never trusts the filename.
      const form = new FormData();
      form.append('sessionToken', sessionToken);
      form.append('venueId', venueId);
      form.append('file', file);

      const { data, error } = await supabase.functions.invoke<{ url?: string; error?: string }>(
        'logo-upload',
        { body: form },
      );
      if (error || !data?.url) {
        throw new Error(data?.error || error?.message || 'Upload failed');
      }

      setSettings((prev) => ({ ...prev, logo: data.url! }));
      toast({ title: 'Logo uploaded', description: 'Logo uploaded and will be saved on save.' });
    } catch (err) {
      console.error('Logo upload failed', err);
      toast({ title: 'Upload failed', description: 'Could not upload logo', variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleColorChange = (field: keyof SettingsData, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" title="Settings">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-console-surface border-console-line text-console-text font-geist w-[95vw] sm:w-full rounded-[10px]">
        <DialogHeader>
          <DialogTitle className="font-geist text-[20px] sm:text-[24px] font-semibold tracking-[-0.025em] text-console-text">Singer Page Settings</DialogTitle>
          <DialogDescription className="font-geist text-[13px] sm:text-[14px] text-console-muted">
            Customize your venue's appearance, layout, and queue rules
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="branding" className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-auto bg-console-bg border border-console-line rounded-[6px] p-1">
            <TabsTrigger value="branding" disabled={isBasic} className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <Palette className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Branding</span>
              <span className="sm:hidden">Brand</span>
            </TabsTrigger>
            <TabsTrigger value="layout" disabled={isBasic} className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <Layout className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Layout</span>
              <span className="sm:hidden">Layout</span>
            </TabsTrigger>
            <TabsTrigger value="announcements" disabled={host?.subscription_level !== 'enterprise'} className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <Megaphone className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Announcements</span>
              <span className="sm:hidden">Announce</span>
            </TabsTrigger>
            <TabsTrigger value="queue" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm">
              <Users className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Queue Rules</span>
              <span className="sm:hidden">Queue</span>
            </TabsTrigger>
          </TabsList>

          {/* Branding & Theming */}
          <TabsContent value="branding" className="space-y-6">
            {isBasic && (
              <Card className="border-console-signal/30 bg-console-signal/10 rounded-[6px]">
                <CardContent className="pt-6">
                  <p className="font-geist text-[13px] text-console-signal">
                    Heads up — Branding and custom theming is only available for Regular and Enterprise tier hosts. Please upgrade your subscription to access this feature.
                  </p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <CardTitle>Colors & Theming</CardTitle>
                <CardDescription>Customize the visual appearance of your singer page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primary-color" className="text-sm sm:text-base">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        disabled={isBasic}
                        id="primary-color"
                        type="color"
                        value={settings.primaryColor}
                        onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                        className="w-12 sm:w-16 h-8 sm:h-10 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <Input
                        disabled={isBasic}
                        value={settings.primaryColor}
                        onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                        placeholder="#10b981"
                        className="bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary flex-1 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondary-color" className="text-sm sm:text-base">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        disabled={isBasic}
                        id="secondary-color"
                        type="color"
                        value={settings.secondaryColor}
                        onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                        className="w-12 sm:w-16 h-8 sm:h-10 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <Input
                        disabled={isBasic}
                        value={settings.secondaryColor}
                        onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                        placeholder="#3b82f6"
                        className="bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary flex-1 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="logo-upload">Header Logo URL</Label>
                  <Input
                    disabled={isBasic}
                    id="logo-upload"
                    value={settings.logo}
                    onChange={(e) => setSettings(prev => ({ ...prev, logo: e.target.value }))}
                    placeholder="https://example.com/logo.png"
                    className="bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                 
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom-css">Custom CSS (Advanced)</Label>
                  <Textarea
                    disabled={isBasic}
                    id="custom-css"
                    value={settings.customCSS}
                    onChange={(e) => setSettings(prev => ({ ...prev, customCSS: e.target.value }))}
                    placeholder=".custom-style { background: linear-gradient(...) }"
                    rows={4}
                    className="input-glow font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Page Layout */}
          <TabsContent value="layout" className="space-y-6">
            {isBasic && (
              <Card className="border-console-signal/30 bg-console-signal/10 rounded-[6px]">
                <CardContent className="pt-6">
                  <p className="font-geist text-[13px] text-console-signal">
                    Heads up — Page layout customization is only available for Regular and Enterprise tier hosts. Please upgrade your subscription to access this feature.
                  </p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <CardTitle>Page Elements</CardTitle>
                <CardDescription>Control what appears on your singer page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="show-queue">Show Live Queue</Label>
                    <Switch
                      id="show-queue"
                      checked={settings.showQueue}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showQueue: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="show-search">Show Search Bar</Label>
                    <Switch
                      id="show-search"
                      checked={settings.showSearch}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showSearch: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="show-songs">Show Song List</Label>
                    <Switch
                      id="show-songs"
                      checked={settings.showSongList}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showSongList: checked }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Song Sorting</Label>
                    <Select value={settings.sortBy} onValueChange={(value) => setSettings(prev => ({ ...prev, sortBy: value }))}>
                      <SelectTrigger className="input-glow">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="title">By Title A-Z</SelectItem>
                        <SelectItem value="artist">By Artist A-Z</SelectItem>
                        <SelectItem value="popular">By Popularity</SelectItem>
                        <SelectItem value="recent">Recently Added</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Text Alignment</Label>
                    <Select value={settings.alignment} onValueChange={(value) => setSettings(prev => ({ ...prev, alignment: value }))}>
                      <SelectTrigger className="input-glow">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements */}
          <TabsContent value="announcements" className="space-y-6">
            {!isEnterprise && (
              <Card className="border-console-signal/30 bg-console-signal/10 rounded-[6px]">
                <CardContent className="pt-6">
                  <p className="font-geist text-[13px] text-console-signal">
                    Heads up — Announcement editing is only available for Enterprise tier hosts. Please upgrade your subscription to access this feature.
                  </p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <CardTitle>Announcements & Banner</CardTitle>
                <CardDescription>Edit the message shown in the singer page banner</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="announcement-text">Banner Text</Label>
                  <Textarea
                    disabled={!isEnterprise}
                    id="announcement-text"
                    value={settings.announcement}
                    onChange={(e) => setSettings(prev => ({ ...prev, announcement: e.target.value }))}
                    placeholder="Welcome to Karaoke Night — sign up for the VIP list!"
                    rows={3}
                    className="input-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="announcement-start">Start Date (optional)</Label>
                    <Input disabled={!isEnterprise} id="announcement-start" type="date" value={settings.announcementStart} onChange={(e) => setSettings(prev => ({ ...prev, announcementStart: e.target.value }))} className="disabled:opacity-50 disabled:cursor-not-allowed" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="announcement-end">End Date (optional)</Label>
                    <Input disabled={!isEnterprise} id="announcement-end" type="date" value={settings.announcementEnd} onChange={(e) => setSettings(prev => ({ ...prev, announcementEnd: e.target.value }))} className="disabled:opacity-50 disabled:cursor-not-allowed" />
                  </div>
                </div>

                <div className="pt-2">
                  <p className="text-sm text-muted-foreground">Preview:</p>
                  <div className="mt-2 bg-card/50 rounded p-3 border border-border">
                    {settings.announcement ? (
                      <p className="text-sm">{settings.announcement}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">No announcement configured</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements tab removed from visibility but infrastructure preserved */}

          {/* Queue Management */}
          <TabsContent value="queue" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Queue Rules</CardTitle>
                <CardDescription>Configure how song requests are handled</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="request-limit">Maximum Requests Per Singer</Label>
                  <Input
                    id="request-limit"
                    type="number"
                    min="1"
                    max="10"
                    value={settings.requestLimit}
                    onChange={(e) => {
                      const v = e.target.value;
                      const n = v === '' ? 0 : parseInt(v, 10);
                      setSettings(prev => ({ ...prev, requestLimit: Number.isNaN(n) ? 0 : n }));
                    }}
                    className="input-glow"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="allow-requests">Accept Singer Requests</Label>
                    <p className="text-sm text-muted-foreground">Allow singers to submit new song requests</p>
                  </div>
                  <Switch
                    id="allow-requests"
                    checked={settings.allowRequests}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, allowRequests: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-remove">Auto-Remove Sung Songs</Label>
                    <p className="text-sm text-muted-foreground">Automatically remove songs from queue when marked as sung</p>
                  </div>
                  <Switch
                    id="auto-remove"
                    checked={settings.autoRemove}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, autoRemove: checked }))}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-6 border-t border-border">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white">
            Save Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HostSettings;